import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [aboutContent, setAboutContent] = useState('Betöltés...');
  const [serviceCategories, setServiceCategories] = useState([]);

  useEffect(() => {
    fetchAboutContent();
    fetchServiceCategories();
  }, []);

  const fetchAboutContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/about`);
      setAboutContent(response.data.content);
    } catch (error) {
      console.error('Failed to fetch about content:', error);
      setAboutContent('Üdvözöllek az oldalamon!');
    }
  };

  const fetchServiceCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/service-categories`);
      setServiceCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch service categories:', error);
    }
  };

  useEffect(() => {
    // Starfield effect
    const canvas = document.getElementById('starfield');
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    const stars = [];
    for (let i = 0; i < 100; i++) {
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 1.5,
        opacity: Math.random()
      });
    }
    
    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach(star => {
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
        ctx.fill();
        
        star.opacity += (Math.random() - 0.5) * 0.02;
        if (star.opacity < 0) star.opacity = 0;
        if (star.opacity > 1) star.opacity = 1;
      });
      requestAnimationFrame(animate);
    }
    
    animate();
    
    window.addEventListener('resize', () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    });
  }, []);

  return (
    <div className="relative min-h-screen">
      <canvas id="starfield" className="fixed inset-0 z-0 pointer-events-none"></canvas>
      
      {/* Hero Section */}
      <section id="top" className="relative min-h-[92vh] flex items-center pt-20">
        <div className="hero-backdrop"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="float mb-6">
            <Sparkles className="w-16 h-16 mx-auto text-yellow-200" />
          </div>
          <h1 className="brand text-6xl md:text-8xl mb-4 glow">Celestine</h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8">
            tudatosság · önismeret · spiritualitás
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/termekek" className="btn-magic rounded-lg px-8 py-3 text-lg">
              Termékek megtekintése
            </Link>
            <a href="#kapcsolat" className="glass rounded-lg px-8 py-3 text-lg hover:bg-white/5 transition">
              Időpontot kérek
            </a>
          </div>
        </div>
      </section>

      {/* Rólam Section */}
      <section id="rolam" className="relative py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="section-title text-4xl md:text-5xl text-center mb-12">
            <span>Rólam</span>
          </h2>
          <div className="glass rounded-2xl p-8 md:p-12">
            <p className="text-lg text-gray-300 leading-relaxed whitespace-pre-line">
              {aboutContent}
            </p>
          </div>
        </div>
      </section>

      {/* Szolgáltatások Section */}
      <section id="szolg" className="relative py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="section-title text-4xl md:text-5xl text-center mb-12">
            <span>Szolgáltatások</span>
          </h2>
          
          {serviceCategories.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {serviceCategories.map((category) => (
                <Link
                  key={category.id}
                  to={`/szolgaltatasok?category=${category.id}`}
                  className="group glass rounded-2xl p-8 hover:border-yellow-200/50 transition-all duration-300 transform hover:-translate-y-1"
                >
                  <div className="flex items-center justify-center mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-yellow-200/20 to-purple-500/20 rounded-full flex items-center justify-center">
                      <Sparkles className="w-10 h-10 text-yellow-200 group-hover:scale-110 transition" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-semibold text-yellow-200 mb-4 text-center group-hover:text-yellow-100 transition">
                    {category.name}
                  </h3>
                  <p className="text-gray-300 text-center text-sm">
                    Kattints a részletekért →
                  </p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 glass rounded-2xl">
              <Sparkles className="w-16 h-16 text-yellow-200/30 mx-auto mb-4" />
              <p className="text-gray-400">Hamarosan elérhető szolgáltatások...</p>
            </div>
          )}

          <div className="text-center mt-12">
            <Link 
              to="/szolgaltatasok" 
              className="inline-block btn-magic rounded-lg px-8 py-3 text-white font-semibold transition"
            >
              Összes szolgáltatás megtekintése
            </Link>
          </div>
        </div>
      </section>

      {/* Kapcsolat Section */}
      <section id="kapcsolat" className="relative py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="section-title text-4xl md:text-5xl mb-8">
            <span>Kapcsolat</span>
          </h2>
          <div className="glass rounded-2xl p-8 md:p-12">
            <p className="text-xl text-gray-300 mb-8">
              Kérdésed van, vagy időpontot szeretnél foglalni?
            </p>
            <a 
              href="mailto:celestinethewitch@gmail.com" 
              className="btn-magic rounded-lg px-8 py-3 text-lg inline-block"
            >
              Írj nekem
            </a>
          </div>
        </div>
      </section>

      <style jsx>{`
        .brand { font-family: "Great Vibes", cursive; }
        .glow { text-shadow: 0 0 12px rgba(255, 255, 255, 0.35), 0 0 24px rgba(199, 146, 12, 0.25); }
        .glass {
          background: linear-gradient(180deg, hsl(0 0% 100% / 0.04), hsl(0 0% 100% / 0.02));
          border: 1px solid hsl(0 0% 100% / 0.06);
          backdrop-filter: blur(6px);
        }
        .section-title {
          font-family: "Great Vibes", cursive;
          text-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
        }
        .section-title span {
          color: #ffffff;
        }
        .hero-backdrop {
          position: absolute;
          inset: 0;
          background: radial-gradient(60% 60% at 50% 40%, rgba(182, 155, 72, 0.12), transparent 65%),
                      radial-gradient(40% 60% at 70% 70%, rgba(115, 91, 208, 0.12), transparent 60%);
          mix-blend-mode: screen;
          pointer-events: none;
        }
        .float {
          animation: float 6s ease-in-out infinite;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        .btn-magic {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          border: 1px solid hsl(0 0% 100% / 0.14);
        }
        .btn-magic:before {
          content: "";
          position: absolute;
          inset: -200%;
          background: conic-gradient(from 0deg, transparent, hsl(45 86% 62% / 0.25), transparent 30%);
          animation: spin 6s linear infinite;
          z-index: -1;
          filter: blur(12px);
        }
        @keyframes spin {
          to { transform: rotate(1turn); }
        }
      `}</style>
    </div>
  );
};

export default Home;